<script setup lang="ts">
import { computed, ref, watch, onMounted, nextTick } from 'vue'
import {
  IElement,
  PageElementOptions,
  DEFAULT_PAGE_DIMENSION,
  OLD_PAGE_HEIGHT,
  OLD_PAGE_WIDTH,
} from '@gohighlevel/ghl-proposals-common'
import BlocksRenderer from '../common/BlocksRenderer.vue'
import FloatingElementRenderer from '../common/FloatingElementRenderer.vue'
import PDFPageSection from './PDFPageSection.vue'

const props = defineProps<{
  page: IElement<PageElementOptions>
  allPagesVersionGreaterThanOne: boolean
  pageIndex: number
  totalPages: number
}>()
// const isPDFView = inject<boolean>('isPDFView')
const pageRef = ref<HTMLElement>()
const pageParentRef = ref<HTMLElement>()
const pageViewHeight = ref()
const footerHeight = ref(0)

const pageHeight = computed(() => {
  if (props.allPagesVersionGreaterThanOne) {
    const { dimensions } =
      props.page.component.options.pageDimensions || DEFAULT_PAGE_DIMENSION
    return dimensions.height
  }
  return OLD_PAGE_HEIGHT
})

const calculatePageHeight = (currentHeight: number) => {
  if (currentHeight > pageHeight.value) {
    const multiple = Math.ceil(currentHeight / pageHeight.value)
    const h = multiple * (pageHeight.value - 96) + multiple * 2 * 48
    pageViewHeight.value = h
  } else {
    pageViewHeight.value = currentHeight
  }
}

watch(pageRef, newValue => {
  if (newValue) {
    setTimeout(() => {
      calculatePageHeight(pageRef.value?.offsetHeight as number)
    }, 200)
  }
})

// Measure heights when page is mounted and after content loads
onMounted(() => {
  // Initial measurement after a delay to ensure elements are rendered
  setTimeout(() => {
    measureHeights()
  }, 500)
})

// Measure when page ref is available
watch(pageRef, () => {
  if (pageRef.value) {
    setTimeout(() => {
      measureHeights()
    }, 300)
  }
})

const backgroundSize = computed(() => {
  if (isPDFPage(props.page)) {
    if (props.allPagesVersionGreaterThanOne) {
      const { dimensions } =
        props.page.component.options.pageDimensions || DEFAULT_PAGE_DIMENSION
      return `${dimensions.width}px ${dimensions.height}px`
    } else {
      return `${OLD_PAGE_WIDTH}px ${OLD_PAGE_HEIGHT}px`
    }
  }
  return props.page?.responsiveStyles?.large?.backgroundSize
})

const isPDFPage = (page: any) => page?.component.options.fromPDF

const pageStyles = computed(() => {
  const backgroundSizeStyle = backgroundSize.value
  return {
    backgroundColor:
      props.page?.responsiveStyles?.large?.backgroundColor || null,
    backgroundImage: `url('${props.page?.component?.options?.src}')`,
    backgroundPosition: props.page?.responsiveStyles?.large?.backgroundPosition,
    backgroundRepeat: props.page?.responsiveStyles?.large?.backgroundRepeat,
    backgroundSize: backgroundSizeStyle,
    opacity: props.page?.responsiveStyles?.large?.opacity / 100 || 1,
  }
})

const getDraggableElement = (children: IElement[]) => {
  if (!children) return []
  return children.filter(element => element && element?.component?.isDraggable)
}

watch(
  pageParentRef,
  updatedPageRef => {
    pageParentRef.value = updatedPageRef
  },
  { immediate: true }
)

const pageContentHeight = computed(() => {
  if (props.allPagesVersionGreaterThanOne) {
    const { dimensions } =
      props.page?.component.options.pageDimensions || DEFAULT_PAGE_DIMENSION
    // Content height is the full page height since padding handles margins
    return `${dimensions.height}px`
  }
  return DEFAULT_PAGE_DIMENSION.dimensions.height + 'px'
})

const getPageDimensions = () => {
  if (props.allPagesVersionGreaterThanOne) {
    return (
      props.page?.component.options.pageDimensions || DEFAULT_PAGE_DIMENSION
    )
  }
  return DEFAULT_PAGE_DIMENSION
}

// Simple function to measure footer height from snapshot element
const measureHeights = () => {
  nextTick(() => {
    // Measure footer from snapshot element
    const footerEl = document.getElementById('pdf-footer-snapshot')
    if (footerEl) {
      const height = footerEl.offsetHeight || footerEl.scrollHeight
      if (height > 0) {
        footerHeight.value = height
      }
    }
  })
}

const pageContentBottomPadding = computed(() => {
  // Use measured footer height, fallback to margin
  if (footerHeight.value > 0) {
    return `${footerHeight.value}px`
  }
  const dimensions = getPageDimensions()
  return `${dimensions.margins.bottom}px`
})

const getHeaderElements = () => {
  return props.page?.component?.options?.headers?.children || []
}

const getFooterElements = () => {
  return props.page?.component?.options?.footers?.children || []
}

const pageNumberText = computed(() => {
  if (!props.page.component.options.enablePageNumbering) return ''

  const pageNum = props.pageIndex + 1
  if (props.page.component.options.pageNumberFormat === 'x-of-y') {
    return `Page ${pageNum} of ${props.totalPages}`
  }
  return `Page ${pageNum}`
})

const pageNumberStyles = computed(() => {
  if (!props.page.component.options.enablePageNumbering) return {}

  const isHeader = props.page.component.options.pageNumberLocation === 'header'
  const styles: Record<string, string | number> = {
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 1,
    fontSize: '12px',
    color: '#666',
    pointerEvents: 'none',
  }

  if (isHeader) {
    styles.top = '10px'
  } else {
    styles.bottom = '10px'
  }

  return styles
})
</script>

<template>
  <div class="page-container" style="display: block" ref="pageRef">
    <!-- Snapshot elements for PDF generation and height measurement -->
    <div
      v-if="pageIndex === 0"
      id="pdf-header-snapshot"
      style="
        opacity: 0;
        pointer-events: none;
        position: absolute;
        top: 0;
        left: 0;
        width: 816px;
        z-index: -1;
      "
    >
      <PDFPageSection :page="page" :elements="getHeaderElements()" />
      <div
        v-if="
          page.component.options.enablePageNumbering &&
          page.component.options.pageNumberLocation === 'header'
        "
        :style="pageNumberStyles"
      >
        {{ pageNumberText }}
      </div>
    </div>

    <div
      v-if="pageIndex === 0"
      id="pdf-footer-snapshot"
      style="
        opacity: 0;
        pointer-events: none;
        position: absolute;
        top: 2000px;
        left: 0;
        width: 816px;
        z-index: -1;
      "
    >
      <PDFPageSection :page="page" :elements="getFooterElements()" />
      <div
        v-if="
          page.component.options.enablePageNumbering &&
          page.component.options.pageNumberLocation === 'footer'
        "
        :style="pageNumberStyles"
      >
        {{ pageNumberText }}
      </div>
    </div>

    <div
      ref="pageParentRef"
      class="page"
      :style="{ minHeight: `${pageViewHeight}px` }"
    >
      <table class="border-collapse page-layout-table">
        <thead class="page-layout-table-head">
          <tr>
            <td class="page-layout-table-cell p-0">
              <div class="page-header-space">
                <div class="page-header header-footer" />
              </div>
            </td>
          </tr>
        </thead>

        <tbody class="page-layout-table-body">
          <tr>
            <td class="page-layout-table-cell p-0">
              <div
                :id="props.page.id"
                class="page-view"
                :style="{
                  paddingBottom: pageContentBottomPadding,
                  boxSizing: 'border-box',
                }"
              >
                <div
                  class="page-content parent"
                  style="min-height: 0; flex: 1 1 0%"
                >
                  <!-- <div
                    class="page-header-content"
                    :style="{
                      marginTop: page.component.options.headers
                        ? '0'
                        : getPageDimensions().margins.top + 'px',
                    }"
                  >
                    <PDFPageSection
                      :page="page"
                      :elements="getHeaderElements()"
                    />
                    <div
                      v-if="
                        page.component.options.enablePageNumbering &&
                        page.component.options.pageNumberLocation === 'header'
                      "
                      :style="pageNumberStyles"
                    >
                      {{ pageNumberText }}
                    </div>
                  </div> -->
                  <div
                    class="document-block"
                    style="
                      flex: 1 1 0%;
                      width: 100%;
                      display: flex;
                      flex-direction: column;
                      min-height: 0;
                    "
                  >
                    <FloatingElementRenderer
                      :type="'signature'"
                      :elements="getDraggableElement(page.children)"
                      :is-parent-page="true"
                      :page-id="page.id"
                      :page="props.page"
                      :page-ref="pageParentRef"
                      :parent-ref="pageParentRef"
                      :crossings="[]"
                    />
                    <template
                      v-for="element in page.children"
                      v-bind:key="'NestedElementRenderer-' + element.id"
                    >
                      <BlocksRenderer
                        :page="page"
                        :page-ref="pageParentRef"
                        :parent-ref="pageParentRef"
                        :element="(element as IElement<any>)"
                        :page-id="(page.id as string)"
                        v-if="!element.component.isDraggable"
                      />
                    </template>
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </tbody>

        <tfoot class="page-layout-table-foot">
          <tr>
            <td class="page-layout-table-cell p-0">
              <div class="page-footer-space">
                <div class="page-footer header-footer">
                  <!-- <div
                    class="page-footer-content"
                    :style="{
                      marginBottom: page.component.options.footers
                        ? '0'
                        : getPageDimensions().margins.bottom + 'px',
                    }"
                  >
                    <PDFPageSection
                      :page="page"
                      :elements="getFooterElements()"
                    />
                    <div
                      v-if="
                        page.component.options.enablePageNumbering &&
                        page.component.options.pageNumberLocation === 'footer'
                      "
                      :style="pageNumberStyles"
                    >
                      {{ pageNumberText }}
                    </div>
                  </div> -->
                </div>
              </div>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.page {
  position: relative;
  &::before {
    position: fixed;
    content: '';
    z-index: 0;
    width: 100%;
    height: 100%;
    will-change: transform;
    transform: translateZ(0px);
    top: 0;
    background-color: v-bind('pageStyles.backgroundColor');
    background-image: v-bind('pageStyles.backgroundImage');
    background-position: v-bind('pageStyles.backgroundPosition');
    background-repeat: v-bind('pageStyles.backgroundRepeat');
    background-size: v-bind('pageStyles.backgroundSize');
    opacity: v-bind('pageStyles.opacity');
  }
}
.page-view {
  min-height: v-bind('pageContentHeight');
  width: 816px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.page-content {
  box-sizing: border-box;
}

.page-header-space {
  height: v-bind('getPageDimensions().margins.top + "px"');
  position: relative;
}

.page-footer-space {
  height: v-bind('getPageDimensions().margins.bottom + "px"');
  position: relative;
}

.page-header {
  position: absolute;
  top: 0;
  width: 100%;
  .page-header-content {
    min-height: 48px;
    box-sizing: border-box;
    margin-top: 0px;
  }
}

.header-footer {
  position: relative;
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
}

.page-footer {
  position: absolute;
  bottom: 0;
  width: 100%;
  .page-footer-content {
    min-height: 48px;
    box-sizing: border-box;
  }
}

/* Prevent table head and foot from taking vertical space */
.page-layout-table-head,
.page-layout-table-foot {
  height: 0;
  min-height: 0;
  line-height: 0;
  border: 0;
  padding: 0;
}

.page-layout-table-head > tr,
.page-layout-table-foot > tr {
  height: 0;
  min-height: 0;
  padding: 0;
}

.page-layout-table-head td,
.page-layout-table-foot td {
  height: 0;
  padding: 0;
  border: none;
}

/* The header/footer placeholders inside them must not expand table height */
.page-header-space,
.page-footer-space {
  height: 0 !important;
  overflow: hidden;
}
</style>

<style lang="scss">
@media print {
  .page-layout-table {
    border-collapse: collapse;
    .page-layout-table-head {
      display: table-header-group;
    }
    .page-layout-table-body {
      display: table-cell;
    }
    .page-layout-table-foot {
      display: table-footer-group;
    }
    .page-layout-table-cell {
      padding: 0px;
      display: block;
    }
  }

  body {
    margin: 0;
  }
}
</style>
